import { Colli } from '@frontend/colli/types';
import { Package, PackageType } from '@frontend/package/types';
import { Transaction } from '@frontend/transaction/types';
import { ScreenTrigger, ScreenType } from '@frontend/wizard/types';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useIdentification, useScanners } from '../../../hooks';
import { useWizardPackage } from '../../hooks/useWizardPackage';
import { transactionToTransactionExtended, useWizardTransaction } from '../../hooks/useWizardTransaction';
import { resolvePickUpColliContext, resolvePickUpPackageContext } from '../../utils/seed-pick-up-colli-context';
import { useScreen } from '../useScreen';

interface ViewProps {
    resolving: boolean;
    onSubmit: () => void;
    submitDisabled: boolean;
    onBack: (screenType: ScreenType) => void;
    error: string | null;
    resetError: () => void;
}

const SENSOR_TIMEOUT = 10000; // 10 seconds in milliseconds

const usePickUpScanQrCode = (): ViewProps => {
    const intl = useIntl();
    const { navigationState, store, onBack, onSubmit: onContinue, updateWizardData } = useScreen();
    const wizardTransaction = useWizardTransaction({
        wizardId: store!.wizard.id,
        store,
        autoFetch: false,
        autoCreate: false
    });
    const { package: _package, localUpdatePackage } = useWizardPackage({
        wizardId: store!.wizard.id,
        store,
        autoFetch: false,
        autoCreate: false
    });

    const [resolving, setResolving] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const sensorTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    useScanners({
        id: 'PICK_UP_SCAN_QR_CODE',
        onDeviceSensorReceived: (code) => {
            setResolving(true);
            sensorTimeoutRef.current = setTimeout(() => {
                setResolving(false);
                // No response received within 10 seconds
                setError(intl.formatMessage({ id: 'HOME.ERRORS.CODE_NOT_FOUND', defaultMessage: 'No matching identification found. Please try again.' }));
                sensorTimeoutRef.current = null;
            }, SENSOR_TIMEOUT);
        }
    });

    useIdentification({
        id: 'PICK_UP_SCAN_QR_CODE',
        onNoMatchingIdentificationFound: () => {
            onFail();
        },
        onUnlinkedIdentificationFound: () => {
            if (sensorTimeoutRef.current) {
                clearTimeout(sensorTimeoutRef.current);
                sensorTimeoutRef.current = null;
            }
            setResolving(false);
            setError(
                intl.formatMessage({
                    id: 'kiosk.wizard.pick-up-scan-qr-code.unlinked-identification',
                    description: 'Error shown when a scanned code is recognized but not linked to anything',
                    defaultMessage: 'This code was recognized but is not linked to anything yet. Please try a different code or contact support.'
                })
            );
        },
        onUserIdentified: () => {
            onFail();
        },
        onPackageIdentified: async (pkg: Package) => {
            if (pkg.type !== PackageType.PICK_UP) {
                onFail();
                return;
            }
            if (sensorTimeoutRef.current) {
                clearTimeout(sensorTimeoutRef.current);
                sensorTimeoutRef.current = null;
            }

            if (!pkg.transaction_id || !navigationState.iot?.account_id) {
                onFail();
                return;
            }

            try {
                const { transaction, collis, packages } = await resolvePickUpPackageContext({
                    accountId: navigationState.iot.account_id,
                    spotId: navigationState.spot?.id ?? navigationState.iot.spot_id,
                    package: pkg
                });

                await wizardTransaction.localUpdateTransaction(transaction);
                updateWizardData({ collis, packages });
                if (packages[0]) {
                    await localUpdatePackage(packages[0]);
                } else {
                    await localUpdatePackage(pkg);
                }

                setResolving(false);

                const nextStep = store?.steps.find((step) => step.screen === ScreenType.PICK_UP_SCAN_QR_CODE && step.trigger === ScreenTrigger.SUBMIT);
                onContinue(nextStep);
            } catch (error) {
                console.error('Failed to resolve package pick-up context:', error);
                onFail();
            }
        },
        onColliIdentified: async (colli: Colli) => {
            if (sensorTimeoutRef.current) {
                clearTimeout(sensorTimeoutRef.current);
                sensorTimeoutRef.current = null;
            }

            if (!colli?.transaction_id || !navigationState.iot?.account_id) {
                onFail();
                return;
            }

            try {
                const { transaction, collis, packages } = await resolvePickUpColliContext({
                    accountId: navigationState.iot.account_id,
                    spotId: navigationState.spot?.id ?? navigationState.iot.spot_id,
                    colli
                });

                await wizardTransaction.localUpdateTransaction(transaction);
                updateWizardData({ collis, packages });
                if (packages[0]) {
                    await localUpdatePackage(packages[0]);
                }

                setResolving(false);

                const nextStep = store?.steps.find((step) => step.screen === ScreenType.PICK_UP_SCAN_QR_CODE && step.trigger === ScreenTrigger.SUBMIT);
                onContinue(nextStep);
            } catch (error) {
                console.error('Failed to resolve colli pick-up context:', error);
                onFail();
            }
        },
        onTransactionIdentified: (transaction: Transaction) => {
            if (sensorTimeoutRef.current) {
                clearTimeout(sensorTimeoutRef.current);
                sensorTimeoutRef.current = null;
            }

            wizardTransaction.localUpdateTransaction(transactionToTransactionExtended(transaction));
            setResolving(false);

            const nextStep = store?.steps.find((step) => step.screen === ScreenType.PICK_UP_SCAN_QR_CODE && step.trigger === ScreenTrigger.SUBMIT);
            onContinue(nextStep);
        }
    });

    const onFail = () => {
        if (sensorTimeoutRef.current) {
            clearTimeout(sensorTimeoutRef.current);
            sensorTimeoutRef.current = null;
        }
        setResolving(false);
        setError(intl.formatMessage({ id: 'HOME.ERRORS.CODE_NOT_FOUND', defaultMessage: 'No matching identification found. Please try again.' }));
    };

    const submitDisabled = (!_package?.id && !wizardTransaction.transaction?.id && !(store?.data.collis?.length > 0)) || resolving || !store;

    const onSubmit = () => {
        if (!submitDisabled) {
            updateWizardData({ package_type: PackageType.PICK_UP });
            const nextStep = store.steps.find((step) => step.screen === ScreenType.PICK_UP_SCAN_QR_CODE && step.trigger === ScreenTrigger.SUBMIT);
            onContinue(nextStep);
        }
    };

    useEffect(() => {
        return () => {
            // Clear timeout on unmount
            if (sensorTimeoutRef.current) {
                clearTimeout(sensorTimeoutRef.current);
                sensorTimeoutRef.current = null;
            }
        };
    }, []);

    return {
        resolving,
        onSubmit,
        submitDisabled,
        onBack,
        error,
        resetError: () => setError(null)
    };
};

export default usePickUpScanQrCode;
